import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

const MAX_NAME_LEN = 80;

export function RenameChatDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialName: string;
  onSubmit: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState(props.initialName);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!props.open) return;
    setName(props.initialName);
    setError(null);
    setIsSaving(false);
  }, [props.open, props.initialName]);

  const trimmed = useMemo(() => name.trim(), [name]);
  const isValid = trimmed.length > 0 && trimmed.length <= MAX_NAME_LEN;

  const save = async () => {
    if (!isValid) return;
    setIsSaving(true);
    setError(null);
    try {
      await props.onSubmit(trimmed);
      props.onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to rename chat");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Rename chat</DialogTitle>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="chat-name">Name</Label>
          <Input
            id="chat-name"
            value={name}
            maxLength={MAX_NAME_LEN}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter a name..."
            disabled={isSaving}
            onKeyDown={(e) => {
              if (e.key === "Enter") void save();
            }}
          />
          <div className="text-xs text-muted-foreground flex items-center justify-between">
            <span>{trimmed.length === 0 ? "Name is required" : ""}</span>
            <span>
              {Math.min(trimmed.length, MAX_NAME_LEN)}/{MAX_NAME_LEN}
            </span>
          </div>
          {error && <div className="text-sm text-destructive">{error}</div>}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={() => props.onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!isValid || isSaving}>
            {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

